import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { authApi, type ParsedError } from '@/lib/api/auth.api';
import { cn } from '@/lib/utils';

type ChangePasswordFormData = {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
};

const inputClass =
  'mt-1 block w-full h-9 px-3 py-1.5 text-sm bg-background border border-border rounded-md focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-colors';

function resolveAuthErrorMessage(err: ParsedError, tAuth: (key: string) => string): string {
  if (err.messageKey?.startsWith('auth.')) {
    const translated = tAuth(err.messageKey.replace(/^auth\./, ''));
    if (translated !== err.messageKey.replace(/^auth\./, '')) return translated;
  }
  return err.message || tAuth('errors.generic');
}

export function ProfileChangePasswordSection() {
  const { t } = useTranslation('profile');
  const { t: tAuth } = useTranslation('auth');

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<ChangePasswordFormData>({
    defaultValues: {
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    },
  });

  const newPassword = watch('newPassword');

  const onSubmit = async (data: ChangePasswordFormData) => {
    try {
      await authApi.changePassword({
        currentPassword: data.currentPassword,
        newPassword: data.newPassword,
      });
      toast.success(t('password_change_success'));
      reset();
    } catch (error) {
      toast.error(resolveAuthErrorMessage(error as ParsedError, tAuth));
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">{t('password_section')}</CardTitle>
        <p className="text-sm text-muted-foreground">{t('password_section_hint')}</p>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 max-w-md" noValidate>
          <div>
            <label htmlFor="profile-current-password" className="block text-sm font-medium">
              {t('current_password')}
            </label>
            <input
              id="profile-current-password"
              type="password"
              autoComplete="current-password"
              {...register('currentPassword', {
                required: tAuth('validation.password_required'),
              })}
              className={cn(inputClass, errors.currentPassword && 'border-destructive')}
            />
            {errors.currentPassword && (
              <p className="mt-1 text-sm text-destructive">{errors.currentPassword.message}</p>
            )}
          </div>

          <div>
            <label htmlFor="profile-new-password" className="block text-sm font-medium">
              {t('new_password')}
            </label>
            <input
              id="profile-new-password"
              type="password"
              autoComplete="new-password"
              {...register('newPassword', {
                required: tAuth('validation.password_required'),
                minLength: {
                  value: 8,
                  message: tAuth('validation.password_min_length'),
                },
              })}
              className={cn(inputClass, errors.newPassword && 'border-destructive')}
            />
            {errors.newPassword && (
              <p className="mt-1 text-sm text-destructive">{errors.newPassword.message}</p>
            )}
          </div>

          <div>
            <label htmlFor="profile-confirm-password" className="block text-sm font-medium">
              {t('confirm_password')}
            </label>
            <input
              id="profile-confirm-password"
              type="password"
              autoComplete="new-password"
              {...register('confirmPassword', {
                required: tAuth('validation.confirm_password_required'),
                validate: (value) =>
                  value === newPassword || tAuth('errors.passwords_mismatch'),
              })}
              className={cn(inputClass, errors.confirmPassword && 'border-destructive')}
            />
            {errors.confirmPassword && (
              <p className="mt-1 text-sm text-destructive">{errors.confirmPassword.message}</p>
            )}
          </div>

          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? t('password_change_saving') : t('password_change_submit')}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}
